import React, { useMemo, useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Stats from './components/Stats';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { ListFilters } from '../../../../interfaces/property.interface';
import { buildHeadCells } from '../../../../utils/methods';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFetch from '../../../../hooks/useFetch';
import CommonService from '../../../../services/common.service';
import UserService from '../../../../services/user.service';
import CustomerService from '../../../../services/customer.service';
import { SortOrder, Modules, AuthRoutes, RoleCodes } from '../../../../enums';
import PropertyService from '../../../../services/property.service';
import { AuthContext } from '../../../../contexts/AuthContext';
import { usePersistentFilters } from '../../../../hooks/usePersistentFilters';


const List: React.FC = () => {
  const { t } = useTranslation();
  const { loggedInUser } = useContext(AuthContext);

  const defaultFilters: ListFilters = {
    searchText: '',
    status: '1',
    assignedTo: '',
    customer: '',
    city: '',
    state: '',
    linkedWithHospitable: '',
    myProperties: '0',
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_properties', defaultFilters);

  // Initialize list state with persisted filters
  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    status: '',
    assignedTo: '',
    customer: '',
    city: '',
    state: '',
    linkedWithHospitable: '',
  });

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.customer,
    VALIDATION_FIELD_MAPPING.managedBy,
    VALIDATION_FIELD_MAPPING.commissionPercentage,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.actions,
  ], 'propertyList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const {
    order, orderBy, setOrder, setOrderBy,
  } = useSorting({ defaultOrder: searchParams.get('order') as SortOrder || SortOrder.asc, defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.name });
  const {
    page, perPage, setPage, setPerPage,
  } = usePagination({ defaultPage: Number(searchParams.get('page')) || 1 });

  useUrlParams({
    type: AuthRoutes.properties, uri: Modules.resources + AuthRoutes.properties, perPage, page, order, orderBy
  });

  const { total, data: properties, loading } = useFetch({
    service: PropertyService,
    func: 'list',
    payload: {
      page, perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.propertyList as any)[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      status: filters.status,
      customerId: filters.customer,
      assignedTo: filters.assignedTo,
      city: filters.city,
      state: filters.state,
      linked_with_hospitable: filters.linkedWithHospitable,
      ...((loggedInUser?.role_code === RoleCodes.system_admin || loggedInUser?.role_code === RoleCodes.admin) && { myProperties: filters.myProperties }),
    },
    dependency: [order, orderBy, page, perPage, filters.searchText, filters.status, filters.customer, filters.assignedTo, filters.myProperties, filters.city, filters.state, filters.linkedWithHospitable]
  });

  const { data: kpis } = useFetch({
    service: PropertyService,
    func: 'kpis',
    payload: null,
    dependency: []
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: []
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status]
  });

  const { data: allLinkedWithHospitable } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.linkedWithHospitable, searchText: '' },
    dependency: []
  });

  const { data: linkedWithHospitable } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.linkedWithHospitable, searchText: dropdownSearchData.linkedWithHospitable },
    dependency: [dropdownSearchData.linkedWithHospitable]
  });

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', is_active: '1' },
    dependency: []
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: dropdownSearchData.assignedTo, is_active: '1' },
    dependency: [dropdownSearchData.assignedTo]
  });

  const { data: allCustomers } = useFetch({
    service: CustomerService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: []
  });

  const { data: customers } = useFetch({
    service: CustomerService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.customer, is_active: '1' },
    dependency: [dropdownSearchData.customer]
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '', stateId: filters.state },
    dependency: [filters.state]
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.city, stateId: filters.state },
    dependency: [dropdownSearchData.city, filters.state]
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: dropdownSearchData.state },
    dependency: [dropdownSearchData.state]
  });

  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const headerSection = <TableHeaderSection total={total} filters={filters} onFilterChange={handleApplyFilters} />

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection, row: Row, data: properties || [], pagination: true, headCells, order, orderBy, page, perPage, total, loading, noDataFoundMessage: t("property.list.pageTexts.table.noDataFoundMessage"), setPage, setPerPage, setOrder, setOrderBy
    };
  }, [headCells, order, orderBy, page, perPage, total, loading, properties, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <>
      <Header />

      <Stats data={kpis} />

      <Filters
        status={status}
        allStatus={allStatus}
        managers={managers}
        allManagers={allManagers}
        customers={customers}
        allCustomers={allCustomers}
        cities={cities}
        allCities={allCities}
        states={states}
        allStates={allStates}
        linkedWithHospitable={linkedWithHospitable}
        allLinkedWithHospitable={allLinkedWithHospitable}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={values} />
    </>
  );
};

export default List;